import { useEffect, useState, type RefObject } from 'react';

// Where the island floats: the shell's `bottom-8` (2rem) plus the `h-14`
// (3.5rem) pagination pill puts the pill's top edge 5.5rem above the viewport
// bottom. The clearance extends past that by roughly the browse pages'
// carousel→pill-bar spacer (10rem at the 1920 design width), so the island
// keeps floating over that whitespace and starts its fade as the next
// section's content (the filter bar) is about to enter — the hide grace +
// exit fade then finish right around when it reaches the pill. Rem-based so
// it tracks the vw-scaled root.
const ISLAND_CLEARANCE_REM = 12;

/**
 * True while `ref`'s bottom edge still reaches down into the island's berth at
 * the bottom of the viewport. The reading-line containment that drives the
 * island measures the middle of the viewport, so a tall section keeps the
 * island long after its own content has scrolled above where the pill actually
 * floats — leaving the pill hovering over whatever section comes next. Gate a
 * registration's `enabled` with this to release the island the moment the
 * section's bottom edge clears the pill instead.
 *
 * SSR-safe (starts true, measures on mount). Pass `enabled: false` to skip the
 * listeners entirely (e.g. mobile, where the section never registers).
 */
export const useSectionReachesIsland = (
    ref: RefObject<HTMLElement | null>,
    enabled: boolean,
): boolean => {
    const [reaches, setReaches] = useState(true);

    useEffect(() => {
        if (!enabled) return;
        let frame = 0;
        const measure = () => {
            frame = 0;
            const el = ref.current;
            if (!el) return;
            const rootFontSize =
                parseFloat(
                    getComputedStyle(document.documentElement).fontSize,
                ) || 16;
            const islandTop =
                window.innerHeight - ISLAND_CLEARANCE_REM * rootFontSize;
            setReaches(el.getBoundingClientRect().bottom > islandTop);
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [ref, enabled]);

    return reaches;
};
